import { useMemo } from 'react';
import { compact } from 'lodash';

import { GENERATION_TOOL, type GenerationTool } from 'src/data/generationTool';
import { PROJECT_TYPE, type ProjectType } from 'src/data/project';
import {
  type MultiPromptTemplate,
  type RecentlyUsedTemplate,
  type SubTemplate,
  TEMPLATE_TYPE,
} from 'src/data/template';
import { createStateSelectorHook } from 'src/lib/sweetState';
import { TemplatesStore } from 'src/store/templates';

export interface RecentlyUsedTemplateCardData {
  id: string;
  projectType: ProjectType;
  generationTool: GenerationTool;
  customToolData?: MultiPromptTemplate;
  subTemplateData?: SubTemplate;
}

const useTemplatesState = createStateSelectorHook(TemplatesStore, ['recentlyUsedTemplates']);

export const useRecentlyUsedTemplatesCards = () => {
  const { recentlyUsedTemplates } = useTemplatesState();

  return useMemo(() => {
    if (!recentlyUsedTemplates) {
      return [];
    }

    return compact(recentlyUsedTemplates.map(mapCard));
  }, [recentlyUsedTemplates]);
};

const mapCard = (
  recentlyUsedTemplate: RecentlyUsedTemplate
): RecentlyUsedTemplateCardData | undefined => {
  const { type, generationTool } = recentlyUsedTemplate;

  switch (type) {
    case TEMPLATE_TYPE.customMultiPrompt: {
      const customTool = recentlyUsedTemplate.customMultiPromptTemplate;
      if (!customTool) {
        return;
      }

      return {
        id: `${generationTool}-customTool-${customTool.id}`,
        projectType: customTool.projectType || PROJECT_TYPE.document,
        generationTool: generationTool || GENERATION_TOOL.customMultiPrompt,
        customToolData: customTool,
      };
    }

    case TEMPLATE_TYPE.subTemplate: {
      const subTemplate = recentlyUsedTemplate.subTemplate;
      if (!subTemplate) {
        return;
      }

      return {
        id: `${generationTool}-subTemplate-${subTemplate.id}`,
        projectType: PROJECT_TYPE.document,
        generationTool,
        subTemplateData: subTemplate,
      };
    }

    case TEMPLATE_TYPE.generationTool: {
      return {
        id: `${generationTool}`,
        projectType:
          generationTool === GENERATION_TOOL.blogDescription
            ? PROJECT_TYPE.blogPostBuilder
            : PROJECT_TYPE.document,
        generationTool,
      };
    }
  }
};
